import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import WORLD_FAMOUS_DESTINATIONS from '../config/worldFamousDestinations';
import './PopularDestinations.css';

const fallbackImages = [
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80',
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1600&q=80',
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1600&q=80',
  'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1600&q=80',
  'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1600&q=80',
  'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=1600&q=80',
];

const PopularDestinations = () => {
  const navigate = useNavigate();
  const destinations = WORLD_FAMOUS_DESTINATIONS;
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);

  const startAutoSlide = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (destinations.length <= 1) return;
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % destinations.length);
    }, 5000);
  }, [destinations.length]);

  useEffect(() => {
    startAutoSlide();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [startAutoSlide]);

  const goTo = (index) => {
    setActiveIndex(index);
    startAutoSlide();
  };

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + destinations.length) % destinations.length);
    startAutoSlide();
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % destinations.length);
    startAutoSlide();
  };

  const getSlideImage = (destination, index) =>
    destination.image || fallbackImages[index % fallbackImages.length];

  return (
    <section className="pd-section">
      <div className="pd-container">
        <div className="pd-header">
          <h2 className="pd-title">Popular Destinations</h2>
          <p className="pd-subtitle">Discover iconic landmarks and must-visit places around the world</p>
        </div>

        <div className="pd-slider">
          {destinations.map((dest, i) => (
            <div
              key={`${dest.name}-${dest.city}`}
              className={`pd-slide ${i === activeIndex ? 'pd-slide--active' : ''}`}
            >
              <img
                className="pd-slide-img"
                src={getSlideImage(dest, i)}
                alt={dest.name}
                loading={i === 0 ? 'eager' : 'lazy'}
                onError={(e) => {
                  e.currentTarget.src = fallbackImages[i % fallbackImages.length];
                }}
              />
              <div className="pd-slide-overlay" />

              <span className="pd-badge">World Destination</span>

              <div className="pd-slide-content">
                <h3 className="pd-slide-name">{dest.name}</h3>
                <p className="pd-slide-location">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {dest.city}{dest.city && dest.state ? ', ' : ''}{dest.state}
                </p>
                {dest.description && (
                  <p className="pd-slide-desc">{dest.description}</p>
                )}
                <button
                  className="pd-slide-btn"
                  onClick={() =>
                    navigate(`/search?q=${encodeURIComponent(dest.searchQuery || dest.city || dest.name)}`)
                  }
                >
                  Explore Stays
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          ))}

          {destinations.length > 1 && (
            <>
              <button className="pd-arrow pd-arrow--left" onClick={goPrev} aria-label="Previous slide">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <button className="pd-arrow pd-arrow--right" onClick={goNext} aria-label="Next slide">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </>
          )}

          {destinations.length > 1 && (
            <div className="pd-dots">
              {destinations.map((_, i) => (
                <button
                  key={i}
                  className={`pd-dot no-min-touch ${i === activeIndex ? 'pd-dot--active' : ''}`}
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PopularDestinations;
